/**
 * utils/lineDiff.js
 * Which transaction lines changed between two versions of a transaction — for its edit log
 * (App.js's buildTxnEditDiff). Pure. Section E1.
 *
 * Lines carry no id of their own, so they used to be compared by POSITION (1st with 1st, 2nd
 * with 2nd…). Removing line A from [A, B, C] then read as "A → B", "B → C", "C removed" — every
 * line below the removed one looked changed. Now lines are matched by WHICH ITEM they are:
 *   1. Align the two lists on `internalCode` with a longest-common-subsequence match, keeping
 *      order (an item that appears twice is matched in order, first with first).
 *   2. A matched pair is "changed" when its qty / price / colly differ (unchanged → nothing).
 *   3. Between two matched pairs, the unmatched lines are a "gap". A gap with the SAME number of
 *      old and new lines is a straight replacement made in place (the line's "ganti" button) →
 *      "swapped" old → new, paired in order, carrying any qty/price difference too. Any other
 *      gap is reported honestly as separate "removed" and "added" lines, never guessed pairs.
 *
 * Returns entries in the shape EditLogTimeline already renders:
 *   { kind: "changed" | "added" | "removed" | "swapped", label, fields: string[] }
 */

// "SG-00001 · SIDE GEAR" — the code makes the entry unambiguous when several items share a name
// (fix A, section E2). Just the code when the line has no name of its own.
const nameOf = (l) => {
  if (!l) return "";
  const code = l.internalCode || "";
  return l.name && l.name !== code ? `${code} · ${l.name}` : code || l.name || "";
};

/** Differences between two lines of the same position/pairing, as "Label: a → b" strings. */
function lineFieldChanges(a, b, fmtQty) {
  const fields = [];
  if (a.qty !== b.qty) fields.push(`Qty: ${fmtQty(a.qty)} → ${fmtQty(b.qty)}`);
  if (a.unitPrice !== b.unitPrice) fields.push(`Harga: ${fmtQty(a.unitPrice)} → ${fmtQty(b.unitPrice)}`);
  if ((a.collyNo ?? null) !== (b.collyNo ?? null)) fields.push(`Colly: ${a.collyNo ?? "—"} → ${b.collyNo ?? "—"}`);
  return fields;
}

/** Index pairs [i, j] of a longest common subsequence of the two code lists, in order. */
function alignByCode(before, after) {
  const n = before.length;
  const m = after.length;
  const L = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      L[i][j] = before[i].internalCode === after[j].internalCode
        ? L[i + 1][j + 1] + 1
        : Math.max(L[i + 1][j], L[i][j + 1]);
    }
  }
  const pairs = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i].internalCode === after[j].internalCode) { pairs.push([i, j]); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) i++;
    else j++;
  }
  return pairs;
}

export function diffTxnLines(beforeLines = [], afterLines = [], fmtQty = String) {
  const before = beforeLines || [];
  const after = afterLines || [];
  const out = [];

  const emitGap = (removed, added) => {
    if (removed.length > 0 && removed.length === added.length) {
      removed.forEach((a, k) => {
        const b = added[k];
        out.push({ kind: "swapped", label: `${nameOf(a)} → ${nameOf(b)}`, fields: lineFieldChanges(a, b, fmtQty) });
      });
      return;
    }
    for (const a of removed) out.push({ kind: "removed", label: nameOf(a), fields: [`Qty dihapus: ${fmtQty(a.qty)}`] });
    for (const b of added) out.push({ kind: "added", label: nameOf(b), fields: [`Qty ditambahkan: ${fmtQty(b.qty)}`] });
  };

  let pi = 0;
  let pj = 0;
  for (const [i, j] of alignByCode(before, after)) {
    emitGap(before.slice(pi, i), after.slice(pj, j));
    const fields = lineFieldChanges(before[i], after[j], fmtQty);
    if (fields.length > 0) out.push({ kind: "changed", label: nameOf(after[j]), fields });
    pi = i + 1;
    pj = j + 1;
  }
  emitGap(before.slice(pi), after.slice(pj));
  return out;
}
